/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Skeleton } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import {
  NEW_ROSTER,
  type RosterChoice,
} from '@sportninja/common/features/competition/operations/utils/addTeamPayload'
import type { RosterChoiceOption } from '@sportninja/common/features/competition/operations/utils/rosterChoices'
import {
  paragraphSmall,
  paragraphXSmall,
  weightSemiBold,
} from 'src/components/css'

const translate = t as (key: string, options?: unknown) => string

interface RosterChoiceStepProps {
  teamName: string
  choices: RosterChoiceOption[]
  /** Roster already tied to this competition — nothing to choose if present. */
  existing: RosterChoiceOption | null
  /** A placeholder team has no roster to clone. */
  isPlaceholder: boolean
  isLoading: boolean
  value: RosterChoice
  onChange: (value: RosterChoice) => void
}

const Entry = ({
  label,
  sublabel,
  isActive,
  onClick,
}: {
  label: string
  sublabel?: string
  isActive: boolean
  onClick?: () => void
}) => (
  <button
    type='button'
    css={entryStyle(isActive, Boolean(onClick))}
    onClick={onClick}
    disabled={!onClick}
    aria-pressed={onClick ? isActive : undefined}
  >
    {/* Drawn rather than antd's Radio, which renders an <input> — invalid
        inside a <button>. Decorative: the button's aria-pressed carries the
        state. Always shown, empty until chosen, so every row reads as one
        option of a pick-one list. */}
    <span css={radioStyle(isActive)} aria-hidden='true' />
    <span css={entryTextStyle}>
      <span css={entryLabelStyle}>{label}</span>
      {sublabel && <span css={entrySubStyle}>{sublabel}</span>}
    </span>
  </button>
)

/**
 * Step two: what roster the team should start this competition with.
 *
 * Only reached for an EXISTING team. Two cases need no decision and say so
 * rather than presenting an empty list: a team already holding a roster for
 * this competition, and a placeholder team, which has none to clone.
 */
const RosterChoiceStep = ({
  teamName,
  choices,
  existing,
  isPlaceholder,
  isLoading,
  value,
  onChange,
}: RosterChoiceStepProps) => {
  'use memo'

  if (isLoading) {
    return <Skeleton active paragraph={{ rows: 3 }} />
  }

  if (existing) {
    return (
      <div css={stepStyle}>
        <p css={leadStyle}>{translate('Web:rosterAlreadyLinked')}</p>
        <Entry label={existing.label} sublabel={existing.sublabel} isActive />
      </div>
    )
  }

  if (isPlaceholder) {
    return (
      <div css={stepStyle}>
        <p css={leadStyle}>
          {translate('Web:placeholderTeamRosterNote', { team: teamName })}
        </p>
      </div>
    )
  }

  const toggle = (next: RosterChoice) => onChange(value === next ? null : next)

  return (
    <div css={stepStyle}>
      <p css={leadStyle}>{translate('Web:rosterChoiceLead')}</p>

      {choices.length > 0 && (
        <>
          <span css={groupLabelStyle}>
            {translate('Web:selectExistingRoster')}
          </span>
          <div css={listStyle} data-testid='roster-list'>
            {choices.map((choice) => (
              <Entry
                key={choice.id}
                label={choice.label}
                sublabel={choice.sublabel}
                isActive={value === choice.id}
                onClick={() => toggle(choice.id)}
              />
            ))}
          </div>
          <span css={orStyle}>{translate('common:or')}</span>
        </>
      )}

      <Entry
        label={translate('Web:createEmptyRoster')}
        isActive={value === NEW_ROSTER}
        onClick={() => toggle(NEW_ROSTER)}
      />
    </div>
  )
}

const stepStyle = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  /* Lets the list below shrink and scroll instead of overflowing the dialog. */
  min-height: 0;
  flex: 1 1 auto;
`

const leadStyle = css`
  ${paragraphSmall}
  margin: 0;
  flex: none;
  color: ${colors.NEUTRAL_50};
`

const groupLabelStyle = css`
  ${weightSemiBold}
  flex: none;
  font-size: 10px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${colors.NEUTRAL_100};
`

/**
 * The only scrolling region in this step. A team can carry many seasons' worth
 * of rosters, so the list scrolls internally rather than growing the dialog —
 * BaseModal's desktop body is `overflow: hidden`, so anything taller than the
 * dialog would be CLIPPED, not scrollable, and the footer buttons would be
 * pushed out of reach.
 *
 * `min-height: 0` is load-bearing: a flex child will not shrink below its
 * content by default, so without it `overflow: auto` never engages and
 * `max-height` is ignored.
 */
const listStyle = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
  min-height: 0;
  flex: 1 1 auto;
  /* Roughly four rows before scrolling — enough to show there are more. */
  max-height: 260px;
`

const orStyle = css`
  ${paragraphXSmall}
  ${weightSemiBold}
  flex: none;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${colors.NEUTRAL_100};
`

const entryStyle = (isActive: boolean, clickable: boolean) => css`
  display: flex;
  flex: none;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border-radius: 6px;
  text-align: left;
  background: ${isActive ? colors.SECONDARY_300 : colors.SECONDARY_400};
  border: 1px solid ${isActive ? colors.PRIMARY : colors.SECONDARY_200};
  cursor: ${clickable ? 'pointer' : 'default'};
`

/**
 * A 16px radio: an empty ring at rest, a PRIMARY ring around a PRIMARY dot
 * when chosen. The dot is an inset shadow, so the ring keeps its size in both
 * states and nothing beside it shifts.
 */
const radioStyle = (isActive: boolean) => css`
  flex: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid ${isActive ? colors.PRIMARY : colors.NEUTRAL_100};
  box-shadow: ${isActive
    ? `inset 0 0 0 3px ${colors.SECONDARY_300}, inset 0 0 0 8px ${colors.PRIMARY}`
    : 'none'};
`

const entryTextStyle = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const entryLabelStyle = css`
  ${paragraphSmall}
  color: ${colors.NEUTRAL_0};
`

const entrySubStyle = css`
  ${paragraphXSmall}
  color: ${colors.NEUTRAL_100};
`

export default RosterChoiceStep
